export const TextEffectsSharedViewerType = 'wixui.TextEffectsShared' as const;

export const TextEffectsSharedData = 'TextEffectsSharedData';

export const TestIds = {
  viewer: {
    link: 'text-effects-shared-link',
    visuallyHidden: 'text-effects-shared-visually-hidden',
    patternUnit: 'text-effects-shared-pattern-unit',
  },
} as const;

export const HEADING_LEVELS = {
  H1: 1,
  H2: 2,
  H3: 3,
  H4: 4,
  H5: 5,
  H6: 6,
  P: 0,
  '⏤': -1,
} as const;

export const DEFAULT_HTML_TAG = 'div' as const;
export const DEFAULT_TEXT_DIRECTION = 'inherit' as const;

export type HeadingLevel = keyof typeof HEADING_LEVELS;

export const COLOR_PICKER_X_OFFSET = 0;
export const COLOR_PICKER_Y_OFFSET = 35;

export const MIN_FONT = 8;
export const MAX_FONT = 999;

export const dataHooks = {
  root: 'text-effect',
  settings: {
    textInput: 'settings-text-input',
    textInputLabel: 'settings-text-input-label',
    textDirection: 'settings-text-direction',
    textDirectionToggle: 'settings-text-direction-toggle',
  },
  design: {
    colorSwatch: 'color-swatch',
    colorSwatches: 'color-swatches',
    themesDropdown: 'text-effects-shared-font-themes-dropdown',
    fontFamilyPicker: 'text-effects-shared-font-family-picker',
    fontSizeInput: 'text-effects-shared-font-size-input',
    textCollapsibleLabel: 'text-effects-shared-text-collapsible-label',
    textStylesList: 'text-effects-shared-text-styles-list',
    textAlignList: 'text-effects-shared-text-align-list',
    textTransformList: 'text-effects-shared-text-transform-list',
    lineHeightInput: 'text-effects-shared-line-height-input',
    letterSpacingInput: 'text-effects-shared-letter-spacing-input',
    maxMinFontSize: 'text-effects-shared-max-min-font-size',
    maxFontSizeInput: 'text-effects-shared-max-font-size-input',
    textDirectionList: 'text-effects-shared-text-direction-list',
    headingTagDropdown: 'text-effects-shared-heading-tag-dropdown',
    isDecorativeCheckbox: 'text-effects-shared-is-decorative-checkbox',
    borderColorSelect: 'text-effects-shared-border-color-select',
    borderWidthSlider: 'text-effects-shared-border-width-slider',
    borderWidthInput: 'text-effects-shared-border-width-input',
    layerSpacingInput: 'text-effects-shared-layer-spacing-input',
    bevelAngleInput: 'text-effects-shared-bevel-angle-input',
    newFontPicker: 'text-effects-shared-new-font-picker',
  },
  patterns: {
    patternsToggle: 'patterns-toggle',
    scopeDropdown: 'patterns-scope-dropdown',
    presetDropdown: 'patterns-preset-dropdown',
    rotationInput: 'patterns-rotation-input',
    offsetInput: 'patterns-offset-input',
    rotationSlider: 'patterns-rotation-slider',
    offsetSlider: 'patterns-offset-slider',
  },
};

export const helpIds = {
  gfpp: 'ae8ce04f-3d77-4811-8804-63c110f42cea',
  settings: 'ae8ce04f-3d77-4811-8804-63c110f42cea',
};

export const blendModes = [
  'normal',
  'color',
  'color-burn',
  'color-dodge',
  'darken',
  'difference',
  'exclusion',
  'hard-light',
  'hue',
  'lighten',
  'luminosity',
  'multiply',
  'overlay',
  'saturation',
  'screen',
  'soft-light',
] as const;

export const translationKeys = {
  dataBindingPanel: {
    title: 'Connect_Panel_Title_TextEffects',
    componentName: 'TextEffects_db_comp_name',
    linkActionTitle: 'Connect_Panel_Connect_TextEffects_Link_Label',
    textConnectLabel: 'Connect_Panel_Connect_TextEffects_Label',
    manageContentPanelDescription: 'TextEffects_db_manage_content',
    gfppActionLabel: 'TextEffects_db_edit_text_label',
  },
  displayName: 'component-editors.wixui.TextArt_DisplayName',
  gfpp: {
    changeText: 'gfpp_MainAction_TextEffects.ChangeText',
    help: 'TextEffects_help_gfpp',
  },
  settingsPanel: {
    header: 'TextEffects.Settings_Header',
    textboxLabel: 'TextEffects.Settings_Textbox_label',
    textboxPlaceholder: 'TextEffects.Setting_Textbox_Placeholder',
    changeDirection: 'TextEffects.Settings_TextBox_Direction',
    leftToRight:
      'TextEffects.Settings_TextBox_Direction_LeftToRight_Label_Hover,',
    rightToLeft:
      'TextEffects.Settings_TextBox_Direction_RightToLeft_Label_Hover',
  },
  designPanel: {
    blendMode: {
      label: 'Inspector-TextEffects.Section-Design.Blend-Mode-Label',
      normal: 'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Normal',
      colorBurn:
        'Inspector-TextEffects.Section-Fill.BlendModeDropdown.ColorBurn',
      multiply: 'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Multiply',
      screen: 'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Screen',
      overlay: 'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Overlay',
      darken: 'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Darken',
      lighten: 'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Lighten',
      colorDodge:
        'Inspector-TextEffects.Section-Fill.BlendModeDropdown.ColorDodge',
      hardLight:
        'Inspector-TextEffects.Section-Fill.BlendModeDropdown.HardLight',
      softLight:
        'Inspector-TextEffects.Section-Fill.BlendModeDropdown.SoftLight',
      difference:
        'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Difference',
      exclusion:
        'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Exclusion',
      hue: 'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Hue',
      saturation:
        'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Saturation',
      color: 'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Color',
      luminosity:
        'Inspector-TextEffects.Section-Fill.BlendModeDropdown.Luminosity',
    },
    fillColors: 'Inspector-TextEffects.Section-Design.Fill-Colors-Label',
    fillColor: 'Inspector-TextEffects.Section-Design.Fill-Color-Label',
    layering: {
      label: 'Inspector-TextEffects.Section-Design.Layering-Label',
      layerCount: 'Inspector-TextEffects.Section-Design.Layer-Colors-Label',
      layerSpacing: 'Inspector-TextEffects.Section-Design.Layer-Spacing-Label',
    },
    border: 'Inspector-TextEffects.Section-Design-Border-Label',
    color: 'Inspector-TextEffects.Section-Design.Colors-Label',
    colorAndOpacity: 'Inspector-TextEffects.Section-Design.Color-Opacity-Label',
    width: 'Inspector-TextEffects.Section-Design.Width-Label',
    angle: 'Inspector-TextEffects.Section-Design.Angle-Label',
    bevelAngle: 'Inspector-TextEffects.Section-Design.Layer-BevelAngle-Label',
    distance: 'Inspector-TextEffects.Section-Design.Distance-Label',
    shadow: 'Inspector-TextEffects.Section-Design.Shadow-Section-Label',
    blur: 'Inspector-TextEffects.Section-Design.Blur-Label',
    glow: 'Inspector-TextEffects.Section-Design.Glow-Label',
    spread: 'Inspector-TextEffects.Section-Design.Spread-Label',
    layerSize: 'Inspector-TextEffects.Section-Design.Layer-Size-Label',
    mask: 'Inspector-TextEffects.Section-Design.Mask-Section-Label',
    maskPosition: 'Inspector-TextEffects.Section-Design.Mask-Position',
    movement: 'Inspector-TextEffects.Section-Design.Movement',
    speed: 'Inspector-TextEffects.Section-Design.Movement.Speed',
    stripe: 'Inspector-TextEffects.Section-Design.Stripe-Section-Label',
    stripeSize: 'Inspector-TextEffects.Section-Design.Stripe-Size',
    text: {
      auto: 'Inspector-TextEffects.Section-Text.Spacing.Auto',
      label: 'Inspector-TextEffects.Section-Design.Text-Section-Label',
      theme: 'Inspector-TextEffects.Section-Text.Theme',
      font: 'Inspector-TextEffects.Section-Text.Font',
      uploadFonts: 'Inspector-TextEffects.Section-Text.Font.Upload-Fonts',
      addLanguage: 'Inspector-TextEffects.Section-Text.Add-Language',
      fontSize: 'Inspector-TextEffects.Section-Text.FontSize',
      minMax: 'Inspector-TextEffects.Section-Text.Scale.Font-Size-MinMax.Input',
      min: 'Inspector-TextEffects.Section-Text.Scale.Font-Size-Min.Input',
      max: 'Inspector-TextEffects.Section-Text.Scale.Font-Size-Max.Input',
      none: 'Inspector-TextEffects.Section-Text.Scale.Font-Size-None.Input',
      spacing: 'Inspector-TextEffects.Section-Text.Spacing',
      styles: 'Inspector-TextEffects.Section-Text.Styles',
      bold: 'Inspector-TextEffects.Section-Text.Styles.Bold',
      italic: 'Inspector-TextEffects.Section-Text.Styles.Italic',
      alignment: 'Inspector-TextEffects.Section-Text.Alignment',
      alignLeft:
        'Inspector-TextEffects.Section-Text.Alignment.Label-Hover.Left',
      alignCenter:
        'Inspector-TextEffects.Section-Text.Alignment.Label-Hover.Center',
      alignRight:
        'Inspector-TextEffects.Section-Text.Alignment.Label-Hover.Right',
      justify:
        'Inspector-TextEffects.Section-Text.Alignment.Label-Hover.Justify',
      rtl: 'Inspector-TextEffects.Section-Text.Alignment.Label-Hover.RightToLeft',
      ltr: 'Inspector-TextEffects.Section-Text.Alignment.Label-Hover.LeftToRight',
      capitalization: 'Inspector-TextEffects.Section-Text.Capitalization',
      noCapitalization: 'Inspector-TextEffects.Section-Text.Label-Hover.None',
      uppercase: 'Inspector-TextEffects.Section-Text.Labe-Hover.UpperCase',
      titleCase: 'Inspector-TextEffects.Section-Text.Label-Hover.TitleCase',
      lowercase: 'Inspector-TextEffects.Section-Text.Label-Hover.LowerCase',
      htmlTag: 'Inspector-TextEffects.Section-Text.HeadingTag',
      decorative: 'Inspector-TextEffects.Section-Text.Decorative.Checkbox',
    },
  },
};
